import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Users, FileText, LogOut, BarChart3, Settings, Mail, Stethoscope, HardHat, Building2, UserCog, FileType2, UserPlus, FolderArchive, Calendar, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { initials } from "@/lib/utils-app";
import BrandLogo from "@/components/BrandLogo";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/colaboradores", label: "Colaboradores", icon: Users, testid: "nav-employees" },
  { to: "/documentos", label: "Documentos", icon: FileText, testid: "nav-documents" },
  { to: "/atestados", label: "Atestados", icon: Stethoscope, testid: "nav-atestados" },
  { to: "/epi", label: "EPI & Uniformes", icon: HardHat, testid: "nav-epi" },
  { to: "/escalas", label: "Painel de Escalas", icon: Calendar, testid: "nav-schedule" },
  { to: "/historico-emails", label: "Histórico Emails", icon: Mail, testid: "nav-history" },
];

// Itens exclusivos do e-mail financeiro (Financeiro/Vales/Salários/Configurações).
const FINANCE_EMAIL = "segurancadotrabalhocolenghi@gmail.com";
const financeNavItems = [
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, testid: "nav-reports" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, testid: "nav-settings" },
];

const adminNavItems = [
  { to: "/checklist", label: "Vistorias (Checklist)", icon: ClipboardCheck, testid: "nav-checklist-admin" },
  { to: "/modelos", label: "Modelos", icon: FileType2, testid: "nav-templates" },
  { to: "/banco-talentos", label: "Banco de Talentos", icon: UserPlus, testid: "nav-talents" },
  { to: "/documentos-empresa", label: "Documentos Empresa", icon: FolderArchive, testid: "nav-company-docs" },
  { to: "/filiais", label: "Filiais", icon: Building2, testid: "nav-branches" },
  { to: "/usuarios", label: "Usuários", icon: UserCog, testid: "nav-users" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isDriver = user?.role === "driver";
  const isFinance = (user?.email || "").trim().toLowerCase() === FINANCE_EMAIL;
  const driverNav = [{ to: "/checklist", label: "Checklist Diário", icon: ClipboardCheck, testid: "nav-checklist" }];
  const fullNav = isDriver
    ? driverNav
    : (isAdmin
        ? [...navItems, ...(isFinance ? financeNavItems : []), ...adminNavItems]
        : [...navItems, ...(isFinance ? financeNavItems : [])]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-brand-50/50">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col bg-white border-r border-slate-200">
        <div className="px-6 py-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <BrandLogo size={36} />
            <div>
              <div className="font-outfit font-semibold text-base tracking-tight">Colenghi</div>
              <div className="text-xs text-slate-500">Transporte & Turismo</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {fullNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-brand-900 text-white"
                    : "text-brand-700 hover:bg-brand-50 hover:text-brand-900"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-brand-900 text-white flex items-center justify-center font-medium text-sm">
              {initials(user?.name || user?.email || "?")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              <div className="text-[11px] text-brand-700 truncate mt-0.5" data-testid="current-user-branch">
                {isAdmin ? "Admin · Todas as filiais" : (isDriver ? "Motorista" : (user?.branch_name ? `Filial: ${user.branch_name}` : "Sem filial"))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo size={32} />
          <span className="font-outfit font-semibold">Colenghi</span>
        </Link>
        <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="mobile-logout-button">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {/* Mobile nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 overflow-x-auto">
        <div className="flex min-w-max">
          {fullNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 px-3 text-[10px] flex-shrink-0 ${
                  isActive ? "text-brand-900 font-semibold" : "text-brand-600"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 md:pt-0 pt-14 pb-20 md:pb-0">
        {children || null}
      </main>
    </div>
  );
}
